import type { CustomerConfigState } from '../../layers/config/shared/types/customer';

/**
 * The base context stamped onto every log so the observability platform can
 * organize/filter by tenant, release and build. Service name + version already
 * ride on `source.{name,version}` (from the frogger `app` option in
 * frogger.options.ts); these are the extra dimensions that live in `ctx`.
 */
export interface FroggerBaseContext {
	/** Deploy environment (FROGGER_ENV / customer.releaseLevel). */
	env: string;
	/** App/deploy version (same value as runtimeConfig.public.version). */
	appVersion: string;
	/** How this build renders: prerender | static-spa | ssr | dev. */
	renderMode: string;
	/** Which runtime emitted the log. */
	runtimeSide: 'client' | 'server';
	customerId?: string;
	customerName?: string;
	brandingId?: string;
	portalType?: string;
	productType?: string;
	releaseLevel?: string;
	apiOrigin?: string;
	/** Per-visit session id (client-only) joining every logger path across a browser tab. */
	session?: string;
}

let cachedSessionId: string | undefined;

function getOrCreateSessionId(): string | undefined {
	if (!import.meta.client) return undefined;
	if (cachedSessionId) return cachedSessionId;
	try {
		const existing = sessionStorage.getItem('lcp-frogger-session');
		cachedSessionId = existing ?? crypto.randomUUID();
		if (!existing) sessionStorage.setItem('lcp-frogger-session', cachedSessionId);
	} catch {
		cachedSessionId = crypto.randomUUID(); // storage blocked (private mode): per-load id
	}
	return cachedSessionId;
}

/**
 * Compute the frogger base context from the pinned tenant + runtime config.
 *
 * In production (per-tenant static build) the registry module bakes the tenant
 * into `appConfig.tenant`, so the full identity is available synchronously at the
 * earliest plugin. In plain dev (no NUXT_TENANT_KEY) the customer fields are
 * simply omitted; service/version/env still resolve.
 */
export function useFroggerBaseContext(): FroggerBaseContext {
	const runtime = useRuntimeConfig();
	const customer: CustomerConfigState | null = resolveStaticCustomerConfig();

	const renderMode = import.meta.prerender
		? 'prerender'
		: import.meta.dev
			? 'dev'
			: customer
				? 'static-spa'
				: 'ssr';

	const session = getOrCreateSessionId();

	return {
		env: String(runtime.public.froggerEnv ?? 'development'),
		appVersion: String(runtime.public.version ?? '0.0.0'),
		renderMode,
		runtimeSide: import.meta.server ? 'server' : 'client',
		...(customer
			? {
					customerId: customer.id,
					customerName: customer.name,
					brandingId: customer.branding?.id,
					portalType: customer.portalType,
					productType: customer.productType,
					releaseLevel: customer.releaseLevel,
					apiOrigin: customer.apiOrigin,
				}
			: {}),
		...(session ? { session } : {}),
	};
}
